import { useCallback, useEffect, useReducer, useRef, useState, type ReactNode } from 'react';
import { registerPlugin } from '@capacitor/core';
import { useAppearance, type Appearance } from './appearance';
import { ApiError } from './api/client';
import { createResourceClient } from './api/resources';
import { describeError } from './api/errors';
import type {
  AccessLevel,
  ChildListingResponse,
  OperationRequest,
  PlanListingResponse,
  PlanResponse,
  SharingResponse,
} from './api/types';
import { siteOriginFor } from './auth/config';
import { followPlanLink } from './auth/planLink';
import {
  AuthFlowError,
  beginLogin,
  clearSession,
  completeLogin,
  describeAuthFlowError,
  endSession,
  getSession,
  getAccessToken,
  isSignedIn,
  markSigningOut,
  restoreSession,
  type Session,
} from './auth/session';
import { createTodoOperations } from './blocks/todo';
import { ConfirmDialog } from './ConfirmDialog';
import { Diagnostics } from './Diagnostics';
import { FALLBACK_PROVIDERS, fetchProviders, type IdentityProviderInfo } from './auth/providers';
import { HomeScreen, type DeletedNotice } from './HomeScreen';
import { useMessages } from './i18n/context';
import { LanguagePicker } from './i18n/LanguagePicker';
import { MoveDialog } from './MoveDialog';
import { PlanEditor } from './PlanEditor';
import { ShareDialog } from './ShareDialog';
import { SharedViewer, type SharedViewerDeps } from './SharedViewer';
import { SignIn } from './SignIn';
import { buildEnv, platform } from './platform';
import { runDiagnostics } from './platform/diagnostics';
import { newId } from './platform/ids';
import {
  defaultDeps as defaultSubscriptionDeps,
  subscribe,
  type SubscriptionDeps,
} from './realtime/subscription';
import { useRealtime } from './realtime/useRealtime';
import { createOperations } from './state/operations';
import { initialState, planReducer } from './state/planReducer';
import { ResourceCatalogue } from './resources/ResourceCatalogue';

// The install's platform port (ADR-029 §2/§4 — the browser page or a native shell, picked
// once in `src/platform/index.ts`), the API client it bound, and the operation builders on its
// id source: the one place the composition touches the host.
const nativeAppearance = registerPlugin<{ set(options: { theme: Appearance }): Promise<void> }>('Appearance');
const client = platform.client;
const resourceClient = createResourceClient({
  baseUrl: import.meta.env.VITE_API_BASE ?? '/api',
  fetch: (input, init) => fetch(input, init),
  getToken: getAccessToken,
  onUnauthorized: clearSession,
});
const ops = createOperations({ newId });
const todoOps = createTodoOperations(ops);

// Where this install's share links point: the page's own origin, or — inside a shell, whose
// page is `https://localhost` — the environment's site (ADR-027 §6).
const linkOrigin = platform.kind === 'web' ? window.location.origin : siteOriginFor(buildEnv);

// Foregrounding (ADR-029 §5): every open realtime subscription registers here, and one
// `onResume` from the host refetches and rotates them all. The web page never raises it.
const resumeListeners = new Set<() => void>();
const resume: NonNullable<SubscriptionDeps['resume']> = (callback) => {
  resumeListeners.add(callback);
  return () => resumeListeners.delete(callback);
};
const boundSubscriptionDeps: SubscriptionDeps = {
  ...defaultSubscriptionDeps,
  negotiate: client.negotiateRealtime,
  fetchPlan: client.getPlan,
};
const subscribeHere: typeof subscribe = (planId, currentVersion, handlers, deps = boundSubscriptionDeps) =>
  subscribe(planId, currentVersion, handlers, { ...deps, resume });

const sharedViewerDeps: SharedViewerDeps = {
  getSharedPlan: client.getSharedPlan,
  getSharedNode: client.getSharedNode,
  getSharedChildren: client.getSharedChildren,
  negotiateSharedRealtime: client.negotiateSharedRealtime,
  subscribe: subscribeHere,
};

// The IdP-free transport probe (ADR-029 §7) — a dev shell only, reached as `?diag=1` through a
// deep link; the share id is dev's seeded public latency probe (ADR-027 §9), not a secret.
const DIAGNOSTICS_AVAILABLE = platform.kind !== 'web' && buildEnv === 'dev';
const DEV_PROBE_SHARE_ID = '17bf5e7e06c9a219a8067d16c75eaeae';
const runDevDiagnostics = () =>
  runDiagnostics({
    auth: platform.auth,
    apiBase: `${siteOriginFor('dev')}/api`,
    fetch: (input, init) => fetch(input, init),
    probeShareId: DEV_PROBE_SHARE_ID,
  });

/** A deep link's query value (`?share=` from the share page, `?plan=` from a legacy page). */
function queryParam(name: string, search = window.location.search): string | null {
  const value = new URLSearchParams(search).get(name);
  return value === null || value === '' ? null : value;
}

/** The share id an https link into the app names — `/p/{shareId}` or `?share=` — if any. */
function shareIdOf(url: URL): string | null {
  if (url.pathname.startsWith('/p/')) {
    const segment = decodeURIComponent(url.pathname.slice('/p/'.length)).replace(/\/+$/, '');
    return segment === '' ? null : segment;
  }
  return queryParam('share', url.search);
}

/**
 * Composition shell (excluded from the unit gate, ADR-015): wires the platform sign-in
 * (ADR-024 launch posture), the reducer, the API client, and the screens together — the
 * home listing, the editor with its sharing and move panels and sub-planlets (ADR-027 slice
 * 4b), and the anonymous shared view behind `?share=` (the share page's "Open in Planlets").
 * The `/auth/callback` path completes the PKCE round-trip on the web; the shells hand the
 * callback, their deep links, foregrounding and the back button in through the platform's
 * events (ADR-029 §4/§5/§6). Every string is the current locale's (ADR-031); the language
 * picker sits at the bottom of every screen, and both alert banners hold the failure itself
 * and word it at render, so they follow a switch too.
 */
export function App() {
  const t = useMessages();
  const g = t.grove;
  const { appearance, setAppearance } = useAppearance();
  const [workspaceView, setWorkspaceView] = useState<'plans' | 'shared' | 'settings'>('plans');
  const [resourceInitialUrn, setResourceInitialUrn] = useState<string | null>(() => queryParam('urn'));
  const resourceReturn = useRef<string | null>(resourceInitialUrn);
  const resourceOpened = useCallback((urn: string | null) => { resourceReturn.current = urn; }, []);
  useEffect(() => {
    if (platform.kind !== 'web') {
      // Older installed shells may not have the appearance bridge yet.
      void nativeAppearance.set({ theme: appearance }).catch(() => undefined);
    }
  }, [appearance]);
  const [state, dispatch] = useReducer(planReducer, initialState);
  const [session, setSessionState] = useState<Session | null>(getSession());
  const [providers, setProviders] = useState<readonly IdentityProviderInfo[]>(FALLBACK_PROVIDERS);
  // A failed sign-in round-trip, worded below in the current language.
  const [authError, setAuthError] = useState<{ cause: unknown } | null>(null);
  const [rows, setRows] = useState<readonly PlanListingResponse[] | null>(null);
  const [deleted, setDeleted] = useState<DeletedNotice | null>(null);
  const [childPlans, setChildPlans] = useState<readonly ChildListingResponse[] | null>(null);
  const [sharing, setSharing] = useState<SharingResponse | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // The session ended under the user (the token expired — launch decision 12): the sign-in
  // screen says so and the next sign-in resumes the plan that was open.
  const [expired, setExpired] = useState(false);
  // The anonymous shared view: from the page's URL, or from a deep link into a shell.
  const [shareId, setShareId] = useState<string | null>(() => queryParam('share'));
  const [diagnostics, setDiagnostics] = useState(
    () => DIAGNOSTICS_AVAILABLE && queryParam('diag') === '1',
  );
  // A shell's identity for the sign-in footer (ADR-029 §7); the page has none.
  const [install, setInstall] = useState<string | null>(null);
  const contentRef = useRef<HTMLElement>(null);

  // Screen changes move keyboard/assistive focus to the new content. The destructive
  // confirmation owns its safe initial focus on Cancel. Plan updates preserve field focus.
  useEffect(() => {
    if (!confirmDelete) contentRef.current?.focus({ preventScroll: true });
  }, [workspaceView, state.planId, session, shareId, diagnostics, shareOpen, moveOpen, confirmDelete]);

  // Every failed request lands here; a 401 means the session is no longer honoured (the client
  // already dropped it wherever the install keeps it).
  const fail = useCallback((error: unknown) => {
    if (error instanceof ApiError && error.status === 401) {
      setSessionState(null);
      setExpired(true);
    }
    dispatch({ kind: 'request-failed', cause: error });
  }, []);

  const refreshList = useCallback(() => {
    setRows(null);
    client
      .listPlans()
      .then(setRows)
      .catch((error: unknown) => {
        setRows([]);
        fail(error);
      });
  }, [fail]);

  // A plan just opened (however): show it and fetch its sub-planlets alongside.
  const opened = useCallback(
    (planId: string, plan: PlanResponse, access: AccessLevel, name: string | null) => {
      setWorkspaceView((current) => current === 'settings' ? 'plans' : current);
      dispatch({ kind: 'plan-loaded', planId, plan, access, name });
      setShareOpen(false);
      setMoveOpen(false);
      setConfirmDelete(false);
      setSharing(null);
      setChildPlans(null);
      client
        .listChildren(planId)
        .then(setChildPlans)
        .catch(() => setChildPlans([]));
    },
    [],
  );

  const open = useCallback(
    (planId: string, name: string | null) => {
      dispatch({ kind: 'request-started' });
      client
        .openPlan(planId)
        // The header is the node's name at resolve time; the listing row is the user index's
        // NameSnapshot (fanned out best-effort on rename, so it can lag) and ReturnTo.name a
        // copy taken before a sign-in — both only fill in where a host stamps none.
        .then(({ plan, access, name: resolved }) => opened(planId, plan, access, resolved ?? name))
        .catch(fail);
    },
    [opened, fail],
  );

  // Plan ids are server-generated (ADR-027 §7): creating is a POST that answers with the
  // fresh id's empty document — the creator owns it.
  const create = useCallback(
    (name: string) => {
      dispatch({ kind: 'request-started' });
      client
        .createPlan(name)
        .then((plan) => opened(plan.id, plan, 'owner', name))
        .catch(fail);
    },
    [opened, fail],
  );

  // The code came back (the page's `/auth/callback`, or the shell's private-use scheme):
  // finish the round-trip; a sign-in that resumed interrupted work reopens the plan it
  // interrupted.
  const finishLogin = useCallback(
    (query: URLSearchParams) => {
      completeLogin(platform, query)
        .then(async (established) => {
          setSessionState(established);
          setExpired(false);
          const back = await platform.takeReturnTo();
          if (back !== null) {
            if (platform.kind === 'web' && back.planId.startsWith('urn:')) setResourceInitialUrn(back.planId);
            else open(back.planId, back.name);
          }
        })
        .catch((error: unknown) => setAuthError({ cause: error }));
    },
    [open],
  );

  useEffect(() => {
    if (window.location.pathname !== '/auth/callback') return;
    const query = new URLSearchParams(window.location.search);
    window.history.replaceState(null, '', '/');
    finishLogin(query);
  }, [finishLogin]);

  // A shell keeps the session for the token's lifetime (ADR-029 §4): adopt it at start. The
  // promise is kept so a link that arrives meanwhile can wait for the answer (followLink).
  const restoring = useRef<Promise<void>>(Promise.resolve());
  useEffect(() => {
    restoring.current = restoreSession(platform).then((stored) => {
      if (stored !== null) setSessionState(stored);
    });
    void platform.describeInstall().then(setInstall);
  }, []);

  // The sign-in buttons follow the auth server's advertised provider catalog.
  useEffect(() => {
    void fetchProviders(platform.auth.issuer).then(setProviders);
  }, []);

  // Push (ADR-030 §2): a signed-in shell registers its device for the notices; the page has no
  // channel. Every sign-in and restore registers again — tokens rotate, and the call is
  // idempotent on the API.
  useEffect(() => {
    if (session !== null) void platform.push?.enable();
  }, [session]);

  // The home screen lists the caller's plans whenever it is showing (ADR-027 §5).
  useEffect(() => {
    if (platform.kind !== 'web' && session !== null && state.planId === null && shareId === null) refreshList();
  }, [session, state.planId, shareId, refreshList]);

  // Live updates (ADR-028 §6): while a plan is open, a receive-only subscription refetches the
  // document when a collaborator's operation moves its version ahead of ours — and retitles it
  // from a collaborator's rename notice (§3 as amended 2026-09-16: the name lives outside the
  // versioned document and a refetch would read it through the backend's node cache, so it is
  // adopted from the notice, never refetched). The user's own rename keeps `plan-renamed`.
  useRealtime(
    state.planId,
    state.plan?.version ?? null,
    {
      onPlan: (plan) => dispatch({ kind: 'plan-refreshed', plan }),
      onRenamed: (planId, name) => dispatch({ kind: 'plan-name-refreshed', planId, name }),
    },
    subscribeHere,
  );

  // A legacy page's ?plan= opens directly once a session exists.
  useEffect(() => {
    if (session === null || platform.kind === 'web') return;
    const fromPage = queryParam('plan');
    if (fromPage !== null) open(fromPage, null);
  }, [session, open]);

  const planId = state.planId;

  // An https link into a shell (ADR-029 §6): a share page or a plan, as the SPA's own URL —
  // and, in a dev shell, the diagnostics route.
  const followLink = (url: URL) => {
    const share = shareIdOf(url);
    if (share !== null) {
      setShareId(share);
      return;
    }
    if (DIAGNOSTICS_AVAILABLE && queryParam('diag', url.search) === '1') {
      setDiagnostics(true);
      return;
    }
    const linked = queryParam('plan', url.search);
    if (linked === null) return;
    setShareId(null);
    // Decided once the restore has settled — a notice tapped on a cold start arrives before it
    // (ADR-030 §6, 2026-09-15); the auth module's session is the truth at that point, and a
    // sign-out under way (the token kept for the push DELETE) counts as signed out.
    void followPlanLink(
      linked,
      restoring.current,
      isSignedIn,
      (id) => open(id, null),
      (id) => platform.rememberReturnTo({ planId: id, name: null }),
    );
  };

  // Foregrounded (ADR-029 §5): a persisted session may have run out meanwhile; otherwise the
  // open subscriptions refetch and rotate.
  const resumed = () => {
    if (session?.expiresAt !== undefined && session.expiresAt <= Date.now()) {
      void endSession(platform);
      setSessionState(null);
      setExpired(true);
      return;
    }
    for (const listener of resumeListeners) listener();
  };

  // The hardware back button (Android): one screen back, or let the app exit.
  const back = (): boolean => {
    if (diagnostics) {
      setDiagnostics(false);
      return true;
    }
    if (shareId !== null) {
      setShareId(null);
      return true;
    }
    if (session === null) return false;
    if (workspaceView === 'settings') {
      setWorkspaceView('plans');
      return true;
    }
    if (shareOpen) {
      setShareOpen(false);
      return true;
    }
    if (moveOpen) {
      setMoveOpen(false);
      return true;
    }
    if (confirmDelete) {
      setConfirmDelete(false);
      return true;
    }
    if (planId !== null) {
      dispatch({ kind: 'plan-closed' });
      return true;
    }
    if (workspaceView === 'shared') {
      setWorkspaceView('plans');
      return true;
    }
    return false;
  };

  // The host's events reach the latest render's handlers; the listener itself lives once.
  const latest = useRef({ finishLogin, followLink, resumed, back });
  latest.current = { finishLogin, followLink, resumed, back };
  useEffect(
    () =>
      platform.listen({
        onAuthCallback: (query) => latest.current.finishLogin(query),
        onDeepLink: (url) => latest.current.followLink(url),
        onResume: () => latest.current.resumed(),
        onBack: () => latest.current.back(),
      }),
    [],
  );

  // A 409 is the server's compare-and-swap loop giving up under concurrent edits (ADR-025):
  // refetch the document once and retry the operation once (ADR-029 §3), then fail normally.
  const apply = (operation: OperationRequest, retry = true) => {
    if (planId === null) return;
    dispatch({ kind: 'request-started' });
    client
      .applyOperation(planId, operation)
      .then((plan) => dispatch({ kind: 'plan-updated', plan }))
      .catch((error: unknown) => {
        if (!retry || !(error instanceof ApiError) || error.status !== 409) {
          fail(error);
          return;
        }
        client
          .getPlan(planId)
          .then((plan) => {
            dispatch({ kind: 'plan-refreshed', plan });
            apply(operation, false);
          })
          .catch(fail);
      });
  };

  const rename = (name: string) => {
    if (planId === null) return;
    dispatch({ kind: 'request-started' });
    client
      .renamePlan(planId, name)
      .then(() => dispatch({ kind: 'plan-renamed', name }))
      .catch(fail);
  };

  const createChild = (name: string) => {
    if (planId === null) return;
    dispatch({ kind: 'request-started' });
    client
      .createChildPlan(planId, name)
      .then((plan) => opened(plan.id, plan, 'owner', name))
      .catch(fail);
  };

  // "Make it mine" (ADR-027 §7): a copy under an owned, server-generated id; the source stays.
  const importCurrent = () => {
    if (planId === null) return;
    const name = t.app.importSuffix(state.name ?? planId);
    dispatch({ kind: 'request-started' });
    client
      .importPlan(planId, name)
      .then((plan) => {
        setWorkspaceView('plans');
        opened(plan.id, plan, 'owner', name);
      })
      .catch(fail);
  };

  // The restorable soft delete (ADR-027 §3), behind an in-DOM confirmation (ADR-029 §1): the
  // home screen offers the undo.
  const remove = () => {
    if (planId === null) return;
    setConfirmDelete(true);
  };

  const deleteConfirmed = () => {
    if (planId === null) return;
    const name = state.name ?? planId;
    dispatch({ kind: 'request-started' });
    client
      .deletePlan(planId)
      .then(() => {
        setDeleted({ id: planId, name });
        dispatch({ kind: 'plan-closed' });
      })
      .catch((error: unknown) => {
        setConfirmDelete(false);
        fail(error);
      });
  };

  const restore = (id: string) => {
    dispatch({ kind: 'request-started' });
    client
      .restorePlan(id)
      .then(() => {
        setDeleted(null);
        dispatch({ kind: 'request-settled' });
        refreshList();
      })
      .catch(fail);
  };

  const loadSharing = (id: string) => {
    setSharing(null);
    client.getSharing(id).then(setSharing).catch(fail);
  };

  const openShare = () => {
    if (planId === null) return;
    setShareOpen(true);
    loadSharing(planId);
  };

  // The move panel (ADR-027 §4) reads the current parent from the owner's view and offers the
  // owned plans of the home listing as destinations — both re-read fresh on open. Unlike the
  // home screen's refresh, a failed listing stays "loading" (with the banner) rather than
  // posing as an empty answer.
  const openMove = () => {
    if (planId === null) return;
    setMoveOpen(true);
    loadSharing(planId);
    setRows(null);
    client.listPlans().then(setRows).catch(fail);
  };

  const move = (parentId: string | null) => {
    if (planId === null) return;
    dispatch({ kind: 'request-started' });
    client
      .movePlan(planId, parentId)
      .then(() => {
        dispatch({ kind: 'request-settled' });
        setMoveOpen(false);
      })
      .catch(fail);
  };

  // Every sharing change round-trips, then re-reads the owner's view (ADR-027 §3/§6).
  const share = (change: Promise<unknown>) => {
    if (planId === null) return;
    dispatch({ kind: 'request-started' });
    change
      .then(() => {
        dispatch({ kind: 'request-settled' });
        loadSharing(planId);
      })
      .catch(fail);
  };

  const signOut = () => {
    markSigningOut();
    setWorkspaceView('plans');
    // Unregister the push device while the session token is still valid.
    const forgotten = platform.push?.disable() ?? Promise.resolve();
    void forgotten.then(() => endSession(platform));
    setSessionState(null);
    setExpired(false);
    setRows(null);
    setDeleted(null);
    setResourceInitialUrn(null);
    resourceReturn.current = null;
    setConfirmDelete(false);
    dispatch({ kind: 'plan-closed' });
  };

  const navigate = (view: 'plans' | 'shared' | 'settings') => {
    setWorkspaceView(view);
    setShareOpen(false);
    setMoveOpen(false);
    setConfirmDelete(false);
    dispatch({ kind: 'plan-closed' });
  };

  const shell = (content: ReactNode, signedIn = false) => (
    <div className={`app-shell${signedIn ? ' has-navigation' : ''}`}>
      <a className="skip-link" href="#main-content">{g.skipContent}</a>
      <header className="app-header">
        <h1 className="brand" aria-label="Planlets">
          <svg viewBox="0 0 64 64" aria-hidden="true">
            <path fill="currentColor" fillRule="evenodd" d="M13 55V29C13 18 22 9 33 9H53V25C53 37 44 45 32 45H25V55H13ZM25 33H30C37 33 41 28 41 21H35C29 21 25 25 25 31V33Z" />
          </svg>
          <span>planlets</span>
        </h1>
        <span className="header-note">{g.headerNote}</span>
        <label className="appearance-control">
          <span>{g.appearance}</span>
          <select aria-label={g.appearance} value={appearance} onChange={(event) => setAppearance(event.target.value as Appearance)}>
            <option value="system">{g.system}</option>
            <option value="light">{g.light}</option>
            <option value="dark">{g.dark}</option>
          </select>
        </label>
      </header>
      {signedIn && (
        <aside className="workspace-sidebar">
          <p className="nav-caption">{g.workspace}</p>
          <nav className="workspace-nav" aria-label={g.workspace}>
            {([
              ['plans', platform.kind === 'web' ? t.resources.catalogue : g.plans, 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z'],
              ['shared', g.shared, 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M22 21v-2a4 4 0 0 0-3-3.87M15 3.13a4 4 0 0 1 0 7.75M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0'],
              ['settings', g.settings, 'M4 6h16M4 12h16M4 18h16M8 3v6M16 9v6M10 15v6'],
            ] as const).map(([view, label, path]) => (
              <button key={view} aria-current={workspaceView === view ? 'page' : undefined} disabled={state.busy} onClick={() => navigate(view)}>
                <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={path} /></svg></span>
                <span>{label}</span>
              </button>
            ))}
          </nav>
          <div className="sidebar-note"><span className="brand-dot" />{g.sidebarNote}</div>
        </aside>
      )}
      <main ref={contentRef} id="main-content" tabIndex={-1} className="app-content">
        {content}
        <footer className="language-footer"><LanguagePicker /></footer>
      </main>
    </div>
  );

  // A WebView that cannot run the app (ADR-029 §2) gets told so.
  const unsupported = platform.unsupported();
  if (unsupported !== null) return shell(<p className="notice notice-error" role="alert">{t.unsupported[unsupported]}</p>);
  if (diagnostics) return shell(<Diagnostics run={runDevDiagnostics} />);
  if (shareId !== null) return shell(<SharedViewer shareId={shareId} deps={sharedViewerDeps} />);
  if (platform.kind === 'web' && session === null && resourceInitialUrn !== null) return shell(<>
    <ResourceCatalogue client={resourceClient} initialUrn={resourceInitialUrn} publicOnly onUnauthorized={fail} onOpened={resourceOpened} />
    <div className="signin-actions">{providers.map((provider) => <button key={provider.id} className="button-secondary" onClick={() => {
      void platform.rememberReturnTo({ planId: resourceReturn.current ?? resourceInitialUrn, name: null }).then(() => beginLogin(platform, provider.id)).catch((cause: unknown) => setAuthError({ cause }));
    }}>{t.signIn.withProvider(provider.displayName)}</button>)}</div>
    {authError !== null && <p role="alert">{describeError(authError.cause, t.errors)}</p>}
  </>);

  const { plan, access } = state;
  const signInFailure = (cause: unknown): string =>
    cause instanceof AuthFlowError
      ? describeAuthFlowError(cause, t.auth)
      : cause instanceof Error
        ? cause.message
        : t.app.signInFailed;

  return shell(
    <>
      {authError !== null && (
        <p className="notice notice-error" role="alert">
          {signInFailure(authError.cause)} <button onClick={() => setAuthError(null)}>{t.app.dismiss}</button>
        </p>
      )}
      {session === null ? (
        <SignIn
          providers={providers}
          install={install}
          notice={
            !expired
              ? null
              : state.planId === null
                ? t.app.sessionExpired
                : t.app.sessionExpiredWith(state.name ?? state.planId)
          }
          onSignIn={(idp) => {
            setAuthError(null);
            const remembered =
              platform.kind === 'web' && resourceReturn.current !== null
                ? platform.rememberReturnTo({ planId: resourceReturn.current, name: null })
                : expired && state.planId !== null
                ? platform.rememberReturnTo({ planId: state.planId, name: state.name })
                : Promise.resolve();
            remembered
              .then(() => beginLogin(platform, idp))
              .catch((error: unknown) => setAuthError({ cause: error }));
          }}
        />
      ) : (
        <>
          <div className="account-line"><p>
            {session.email !== null ? t.app.signedIn(session.email) : t.app.signedInWithoutEmail}{' '}
          </p><button className="button-quiet" onClick={signOut}>{t.app.signOut}</button></div>
          {state.error !== null && (
            <p className="notice notice-error" role="alert">
              {describeError(state.error.cause, t.errors)}{' '}
              <button onClick={() => dispatch({ kind: 'error-cleared' })}>{t.app.dismiss}</button>
            </p>
          )}
          {workspaceView === 'settings' ? (
            <section className="settings-screen">
              <div className="page-heading"><div><p className="eyebrow">{g.settingsEyebrow}</p><h2>{g.settings}</h2><p className="muted">{g.settingsDescription}</p></div></div>
              <section className="card settings-card" aria-labelledby="appearance-title">
                <h3 id="appearance-title">{g.appearance}</h3>
                <p className="muted">{g.appearanceDescription}</p>
                <fieldset className="appearance-options"><legend>{g.colorMode}</legend>
                  {(['system', 'light', 'dark'] as const).map((choice) => (
                    <label key={choice} className="appearance-option" data-selected={appearance === choice}>
                      <span className={`theme-swatch theme-swatch-${choice}`} aria-hidden="true"><span /><span /><span /></span>
                      <span><input type="radio" name="color-mode" value={choice} checked={appearance === choice} onChange={() => setAppearance(choice)} />{g[choice]}</span>
                    </label>
                  ))}
                </fieldset>
              </section>
              <section className="card settings-card"><h3>{g.account}</h3><p className="muted">{g.accountDescription}</p><p>{session.email ?? t.app.signedInWithoutEmail}</p></section>
            </section>
          ) : platform.kind === 'web' ? (
            <ResourceCatalogue key={`${workspaceView}:${resourceInitialUrn ?? ''}`} client={resourceClient} initialUrn={resourceInitialUrn} scope={workspaceView === 'shared' ? 'shared' : 'all'} onUnauthorized={fail} onOpened={resourceOpened} />
          ) : planId === null || plan === null || access === null ? (
            <HomeScreen
              scope={workspaceView === 'shared' ? 'shared' : 'all'}
              rows={workspaceView === 'shared' ? rows?.filter((row) => row.role !== 'owner') ?? null : rows}
              busy={state.busy}
              deleted={deleted}
              onOpen={open}
              onCreate={create}
              onRestore={restore}
              onRefresh={refreshList}
            />
          ) : shareOpen ? (
            <ShareDialog
              sharing={sharing}
              busy={state.busy}
              origin={linkOrigin}
              onSetPublic={(mode) => share(client.setPublic(planId, mode))}
              onRotate={() => share(client.rotateShareLink(planId))}
              onSetGrant={(email, role) => share(client.setGrant(planId, email, role))}
              onRemoveGrant={(email) => share(client.removeGrant(planId, email))}
              onClose={() => setShareOpen(false)}
            />
          ) : confirmDelete ? (
            <ConfirmDialog
              title={t.app.deleteTitle(state.name ?? planId)}
              message={t.app.deleteMessage}
              confirmLabel={t.app.delete}
              busy={state.busy}
              onConfirm={deleteConfirmed}
              onCancel={() => setConfirmDelete(false)}
            />
          ) : moveOpen ? (
            <MoveDialog
              planId={planId}
              name={state.name ?? planId}
              sharing={sharing}
              rows={rows}
              busy={state.busy}
              onMove={move}
              onClose={() => setMoveOpen(false)}
            />
          ) : (
            <PlanEditor
              plan={plan}
              name={state.name}
              access={access}
              busy={state.busy}
              childPlans={childPlans}
              onAddTopLevel={(type, title) => apply(ops.makeAdd(type, title))}
              onRename={rename}
              onShare={openShare}
              onMove={openMove}
              onDelete={remove}
              onImport={importCurrent}
              onClose={() => dispatch({ kind: 'plan-closed' })}
              onOpenChild={open}
              onCreateChild={createChild}
              callbacks={{
                onAddChild: (parentId) => apply(ops.makeAdd('task', t.app.newTaskTitle, parentId)),
                onRename: (componentId, title) => apply(ops.makeEdit(componentId, 'title', title)),
                onMove: (componentId, index, parentId) =>
                  apply(ops.makeMove(componentId, index, parentId)),
                onRemove: (componentId) => apply(ops.makeRemove(componentId)),
              }}
              todo={{
                onAddTask: (listId, title) => apply(todoOps.addTask(listId, title)),
                onToggle: (taskId, done) => apply(todoOps.setDone(taskId, done)),
                onRenameTask: (taskId, title) => apply(todoOps.renameTask(taskId, title)),
                onMoveTask: (listId, taskId, index) => apply(todoOps.moveTask(listId, taskId, index)),
                onRemoveTask: (taskId) => apply(todoOps.removeTask(taskId)),
              }}
            />
          )}
        </>
      )}
    </>,
    session !== null,
  );
}
